import * as Sentry from '@sentry/react';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { RoutesEnum } from 'shared/routes';
import { FeatureFlag } from 'src/shared/enums';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectMap3dWorkspace,
  setShowUnsavedFeaturesModal,
} from '../map-3d/shared/map-3d-slices';
import { OrgSettingsModal } from '../org-settings';
import { SettingsModal } from '../settings';
import { useLoggedUser } from '../shared/api';
import { GlobalContext, HeaderTitleContext } from '../shared/context';
import { UserLocalDataManager, isAppOpenInIframe } from '../shared/helpers';
import {
  AccordionSideNav,
  ChatbaseWidget,
  Header,
  HeaderTitle,
} from './components';
import type {
  NavItemData,
  SideCardType,
} from './components/accordion-sidenav/types';
import { noHeaderPaths } from './constants';
import { navItems } from './helpers';
import { NavItemName } from './helpers/enums';

const initialSideCardState: SideCardType = {
  primary: false,
  secondary: false,
};

export const Home: React.FC = () => {
  // Contexts.
  const { setLoggedUser, loggedUser } = useContext(GlobalContext);

  // States.
  const [headerTitle, setHeaderTitle] = useState('');
  const [siteName, setSiteName] = useState('');
  const [headerBackButtonRoute, setHeaderBackButtonRoute] = useState('');
  const [showSideCard, setShowSideCard] =
    useState<SideCardType>(initialSideCardState);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showOrgSettingsModal, setShowOrgSettingsModal] = useState(false);
  const location = useLocation();

  // Dispatchers.
  const dispatch = useAppDispatch();

  // Selectors.
  const { drawingTools } = useAppSelector(selectMap3dWorkspace);

  // Variables.
  const nonFullFrameRoutes: string[] = [
    `${RoutesEnum.Dashboard}/${RoutesEnum.DashboardPlanVsActual}`,
  ];

  const enableFullFrame =
    !headerTitle && !nonFullFrameRoutes.includes(location.pathname);

  // Hooks.
  // Enabled only when org access token is not present.
  const { data: loggedUserResponse, isSuccess: loggedUserIsSuccess } =
    useLoggedUser(!isAppOpenInIframe());

  // useEffects.
  useEffect(() => {
    if (loggedUserIsSuccess && loggedUserResponse) {
      setLoggedUser(loggedUserResponse.data);
      const userName =
        loggedUserResponse.data.firstName +
        ' ' +
        loggedUserResponse.data.lastName;
      Sentry.setUser({
        email: loggedUserResponse.data.email,
        username: userName,
        ip_address: '{{auto}}',
      });
      UserLocalDataManager.saveUserName(userName);
    }
  }, [loggedUserResponse]);

  useEffect(() => {
    if (
      !isEmpty(headerTitle) &&
      noHeaderPaths.some((path: string) => location.pathname.startsWith(path))
    ) {
      setHeaderTitle('');
    }
  }, [location]);

  // Handlers.
  const openSideNavFunc = () => {
    setShowSideCard({ primary: true, secondary: false });
  };

  const closeSideNav = () => {
    setShowSideCard({ primary: false, secondary: false });
  };

  const onClickSideNavItem = (navItemData: NavItemData) => {
    if (
      navItemData.itemName !== NavItemName.Workspace &&
      drawingTools?.checkUnsavedFeatures()
    ) {
      dispatch(setShowUnsavedFeaturesModal(true));
      return;
    }

    if (navItemData.itemName === NavItemName.OrganisationSettings) {
      openOrgSettingsModal();
    }

    closeSideNav();
  };

  // Modal Handlers.
  const closeSettingsModal = () => setShowSettingsModal(false);
  const openSettingsModal = () => setShowSettingsModal(true);

  const closeOrgSettingsModal = () => setShowOrgSettingsModal(false);
  const openOrgSettingsModal = () => setShowOrgSettingsModal(true);

  return (
    <>
      <div className="home">
        <HeaderTitleContext.Provider
          value={{
            siteName,
            setSiteName,
            headerTitle,
            setHeaderTitle,
            showSideCard,
            setShowSideCard,
            openOrgSettingsModal,
            headerBackButtonRoute: headerBackButtonRoute,
            setHeaderBackButtonRoute: setHeaderBackButtonRoute,
          }}
        >
          {!isAppOpenInIframe() && (
            <>
              <Header
                onClick={openSideNavFunc}
                showSideCard={showSideCard}
                onClose={closeSideNav}
                openSettingsModal={openSettingsModal}
              />

              <AccordionSideNav
                showSideCard={showSideCard}
                onClose={closeSideNav}
                onClickSideNavItem={onClickSideNavItem}
                navItems={navItems}
              />
            </>
          )}
          <div
            className={classNames([
              'screens-container',
              enableFullFrame && 'full-frame',
            ])}
          >
            {!isAppOpenInIframe() && (
              <>
                <SettingsModal
                  show={showSettingsModal}
                  onClose={closeSettingsModal}
                />

                <OrgSettingsModal
                  show={showOrgSettingsModal}
                  onClose={closeOrgSettingsModal}
                />
              </>
            )}

            {headerTitle && <HeaderTitle text={headerTitle} />}
            <Outlet />
          </div>
        </HeaderTitleContext.Provider>
        {loggedUser?.featureFlags[FeatureFlag.AereoSupportAIBot] && (
          <ChatbaseWidget />
        )}
      </div>
    </>
  );
};
